<template>
	<v-dialog 
		v-model="dialog"
		max-width="1200"
		scrollable
		:fullscreen="fullScreen || $vuetify.breakpoint.xsOnly">
        <v-card class="background-2 round-card" :class="[$vuetify.breakpoint.smAndDown || fullScreen ? 'rounded-0' : 'round-card']">
            <v-card-title class="pa-3 background-1">
				<v-icon left>mdi-image-multiple</v-icon>
				<p class="pl-2 pl-xl-3">Galleria Media Conversazione</p>
				<v-spacer></v-spacer>
				<v-tooltip top>
					<template v-slot:activator="{ on: tooltip }">					
						<v-btn v-on="{ ...tooltip }" icon @click.native.stop="fullScreen = !fullScreen">
							<v-icon>fullscreen</v-icon>
						</v-btn>
					</template>
					<span>Fullscreen</span>
				</v-tooltip>  
				<v-btn icon @click="dialog = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>
            <v-divider></v-divider>
			<v-card-subtitle class="pa-3">
				<v-chip-group v-model="selectedFilter" mandatory active-class="primary--text">
					<v-chip value="all">
						Tutti ({{ (mediaData.all && mediaData.all.length) || 0 }})
					</v-chip>
					<v-chip value="images">
						<v-icon left small>mdi-image</v-icon>
						Immagini ({{ (mediaData.images && mediaData.images.length) || 0 }})
					</v-chip>
					<v-chip value="videos">
						<v-icon left small>mdi-video</v-icon>
						Video ({{ (mediaData.videos && mediaData.videos.length) || 0 }})
					</v-chip>
					<v-chip value="audio">
						<v-icon left small>mdi-music</v-icon>
						Audio ({{ (mediaData.audio && mediaData.audio.length) || 0 }})
					</v-chip>
					<v-chip value="documents">
						<v-icon left small>mdi-file-document</v-icon>
						Documenti ({{ (mediaData.documents && mediaData.documents.length) || 0 }})
					</v-chip>
				</v-chip-group>
			</v-card-subtitle>

			<v-divider></v-divider>

			<v-card-text :style="{ maxHeight: fullScreen ? '100vh' : '600px' }" class="pt-6">
				<v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

				<div v-if="!loading && filteredMedia.length === 0" class="text-center pa-8">
					<v-icon size="64" color="grey">mdi-image-off</v-icon>
					<p class="text-h6 grey--text mt-4">Nessun media trovato</p>
				</div>

				<v-row v-else>
					<v-col
						v-for="item in filteredMedia"
						:key="item.id"
						cols="12"
						sm="6"
						md="4"
					>
						<v-card
							class="media-card round-card"
							:data-media-id="item.id"
							@click="openMedia(item)"
						>
							<!-- Image -->
							<v-img
								v-if="['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(item.file_type)"
								:src="item.file_url"
								height="200"
								class="media-thumbnail"
							>
								<template v-slot:placeholder>
									<v-row class="fill-height ma-0" align="center" justify="center">
										<v-progress-circular indeterminate color="primary"></v-progress-circular>
									</v-row>
								</template>
							</v-img>

							<!-- Video Thumbnail -->
							<div
								v-else-if="['mp4', '3gp', 'mov', 'avi'].includes(item.file_type)"
								class="video-thumbnail d-flex align-center justify-center"
							>
								<v-icon size="64" color="white">mdi-play-circle</v-icon>
							</div>

							<!-- Audio Thumbnail -->
							<div
								v-else-if="['ogg', 'mp3', 'm4a', 'amr', 'wav'].includes(item.file_type)"
								class="audio-thumbnail d-flex align-center justify-center"
							>
								<v-icon size="64" color="white">mdi-music</v-icon>
							</div>

							<!-- Document Thumbnail -->
							<div
								v-else
								class="document-thumbnail d-flex align-center justify-center"
							>
								<v-icon size="64" color="white">mdi-file-document</v-icon>
							</div>
							<v-divider></v-divider>
							<v-card-text class="pa-2">
								<div class="text-truncate text-caption">
									{{ item.file_name }}
								</div>
								<div class="text-caption grey--text">
									{{ formatFileSize(item.file_size) }}
									<v-chip
										x-small
										:color="item.direction === 'outbound' ? 'green' : 'blue'"
										dark
										class="ml-2"
									>
										{{ item.direction === 'outbound' ? 'Inviato' : 'Ricevuto' }}
									</v-chip>
								</div>
								<div class="text-caption grey--text">
									{{ formatDate(item.created_at) }}
								</div>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
			</v-card-text>
			<v-divider class="mt-4"></v-divider>
			<v-card-actions class="px-4 pb-2 background-1">
				<v-spacer></v-spacer>
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn v-bind="attrs" v-on="on" color="primary" text @click="dialog = false">Chiudi</v-btn>
					</template>
					<span>Esc</span>
				</v-tooltip>
			</v-card-actions>
		</v-card>

		<!-- Media Viewer Dialog -->
		<v-dialog
			v-model="viewerDialog"
			max-width="800"
			:fullscreen="mediaViewerFullScreen || $vuetify.breakpoint.xsOnly">
			<v-card
				v-if="selectedMedia"
				class="background-2 round-card d-flex flex-column"
				:class="[mediaViewerFullScreen ? 'rounded-0 media-viewer-fullscreen' : 'round-card']"
			>
            	<v-card-title class="pa-3 background-1 flex-grow-0">
					<p class="pl-2 pl-xl-3">{{ selectedMedia.file_name }}</p>
					<v-spacer></v-spacer>
					<v-tooltip top>
						<template v-slot:activator="{ on: tooltip }">
							<v-btn v-on="{ ...tooltip }" icon @click.native.stop="mediaViewerFullScreen = !mediaViewerFullScreen">
								<v-icon>fullscreen</v-icon>
							</v-btn>
						</template>
						<span>Fullscreen</span>
					</v-tooltip>
					<v-btn icon @click="viewerDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>

				<v-card-text
					class="flex-grow-1 d-flex align-center justify-center pa-6"
					:class="mediaViewerFullScreen ? 'media-content-fullscreen' : 'media-content-normal'"
				>
					<!-- Image -->
					<v-img
						v-if="['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(selectedMedia.file_type)"
						:src="selectedMedia.file_url"
						contain
						class="media-image-viewer"
					></v-img>

					<!-- Video -->
					<video
						v-else-if="['mp4', '3gp', 'mov', 'avi'].includes(selectedMedia.file_type)"
						:src="selectedMedia.file_url"
						controls
						class="media-video-viewer"
					></video>

					<!-- Audio -->
					<div v-else-if="['ogg', 'mp3', 'm4a', 'amr', 'wav'].includes(selectedMedia.file_type)" class="pa-8 text-center">
						<v-icon size="100" color="primary">mdi-music</v-icon>
						<audio
							:src="selectedMedia.file_url"
							controls
							class="mt-4 media-audio-viewer"
						></audio>
					</div>

					<!-- Document -->
					<div v-else class="pa-8 text-center">
						<v-icon size="100" color="primary">mdi-file-document</v-icon>
						<p class="text-h6 mt-4">{{ selectedMedia.file_name }}</p>
					</div>
				</v-card-text>

				<v-divider></v-divider>
				<v-card-actions class="px-4 pb-2 background-1 flex-grow-0">
					<v-spacer></v-spacer>
					<v-btn color="primary" @click="downloadMedia">
						<v-icon left>mdi-download</v-icon>
						Scarica
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-dialog>
</template>

<script>
export default {
	name: 'MediaGallery',

	props: {
		value: Boolean,
		conversationId: {
			type: Number,
			required: true
		}
	},

	data: () => ({
		loading: false,
		selectedFilter: 'all',
		mediaData: {
			all: [],
			images: [],
			videos: [],
			audio: [],
			documents: []
		},
		viewerDialog: false,
		selectedMedia: null,
        fullScreen: false,
		mediaViewerFullScreen: false,
		loadedMediaIds: new Set(), // Track quali media sono già stati caricati
		observer: null // Intersection Observer per lazy loading
	}),

	computed: {
		dialog: {
			get() {
				return this.value
			},
			set(val) {
				this.$emit('input', val)
			}
		},

		filteredMedia() {
			return this.mediaData[this.selectedFilter] || []
		}
	},

	watch: {
		value(val) {
			if (val) {
				this.loadMedia()
			}
		},

		selectedFilter() {
			// Quando cambia il filtro, riattiva observer per i nuovi elementi visibili
			this.$nextTick(() => {
				this.setupLazyLoading()
			})
		}
	},

	beforeDestroy() {
		// Cleanup Intersection Observer
		if (this.observer) {
			this.observer.disconnect()
		}

		// Revoca tutti i blob URL per liberare memoria
		Object.values(this.mediaData).forEach(mediaArray => {
			mediaArray.forEach(media => {
				if (media.file_url && media.file_url.startsWith('blob:')) {
					URL.revokeObjectURL(media.file_url)
				}
			})
		})
	},

	methods: {
		async loadMedia() {
			this.loading = true
			try {
				const response = await this.axios.get(`/whatsappconversations/${this.conversationId}/media`)

				// Salva solo i metadati, non caricare i blob subito
				const addPlaceholder = (mediaArray) => {
					return mediaArray.map(item => ({
						...item,
						original_url: item.file_url,
						file_url: null, // Sarà caricato con lazy loading
						loading: false
					}))
				}

				this.mediaData = {
					all: addPlaceholder(response.data.media.all),
					images: addPlaceholder(response.data.media.images),
					videos: addPlaceholder(response.data.media.videos),
					audio: addPlaceholder(response.data.media.audio),
					documents: addPlaceholder(response.data.media.documents)
				}

				// Setup Intersection Observer per lazy loading
				// Usa setTimeout per dare tempo al dialog di essere montato
				setTimeout(() => {
					this.setupLazyLoading()
				}, 100)

			} catch (error) {
				console.error('Error loading media:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore durante il caricamento della galleria media',
					value: true,
					color: 'error'
				})
			} finally {
				this.loading = false
			}
		},

		setupLazyLoading() {
			// Cleanup observer precedente
			if (this.observer) {
				this.observer.disconnect()
			}

			this.$nextTick(() => {
				// Trova il container scrollabile del dialog
				// Usa document.querySelector perché Vuetify renderizza i dialoghi fuori da $el
				const scrollContainer = document.querySelector('.v-dialog--active .v-card__text')

				if (!scrollContainer) {
					console.warn('Scroll container not found for lazy loading')
					return
				}

				// Crea nuovo Intersection Observer
				this.observer = new IntersectionObserver((entries) => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							const mediaId = entry.target.dataset.mediaId
							if (mediaId) {
								this.loadMediaBlob(parseInt(mediaId))
							}
						}
					})
				}, {
					root: scrollContainer, // Usa il container scrollabile del dialog come root
					rootMargin: '100px', // Inizia a caricare 100px prima che sia visibile
					threshold: 0.01
				})

				// Osserva tutti i media card
				const mediaCards = document.querySelectorAll('.media-card[data-media-id]')
				mediaCards.forEach(card => this.observer.observe(card))
			})
		},

		async loadMediaBlob(mediaId) {
			// Se già caricato, skip
			if (this.loadedMediaIds.has(mediaId)) return

			this.loadedMediaIds.add(mediaId)

			// Trova il media in tutte le categorie
			const findAndUpdate = (mediaArray) => {
				const media = mediaArray.find(m => m.id === mediaId)
				if (media && !media.file_url) {
					this.$set(media, 'loading', true)

					// Carica il blob
					this.axios.get(media.original_url, { responseType: 'blob' })
						.then(response => {
							const blobUrl = URL.createObjectURL(response.data)
							this.$set(media, 'file_url', blobUrl)
							this.$set(media, 'loading', false)
						})
						.catch(error => {
							console.error('Error loading media blob:', error)
							this.$set(media, 'error', true)
							this.$set(media, 'loading', false)
						})
				}
			}

			// Aggiorna in tutte le categorie
			findAndUpdate(this.mediaData.all)
			findAndUpdate(this.mediaData.images)
			findAndUpdate(this.mediaData.videos)
			findAndUpdate(this.mediaData.audio)
			findAndUpdate(this.mediaData.documents)
		},

		openMedia(item) {
			this.selectedMedia = item
			this.viewerDialog = true
		},

		async downloadMedia() {
			if (!this.selectedMedia) return

			try {
				// Usa original_url se disponibile, altrimenti file_url
				const url = this.selectedMedia.original_url || this.selectedMedia.file_url
				const response = await this.axios.get(url, {
					responseType: 'blob'
				})

				// Crea link temporaneo per download
				const blob = new Blob([response.data])
				const link = document.createElement('a')
				link.href = URL.createObjectURL(blob)
				link.download = this.selectedMedia.file_name
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(link.href)
			} catch (error) {
				console.error('Error downloading media:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore durante il download del file',
					value: true,
					color: 'error'
				})
			}
		},

		formatFileSize(bytes) {
			if (!bytes) return '0 B'
			const k = 1024
			const sizes = ['B', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
		},

		formatDate(date) {
			if (!date) return ''
			const d = new Date(date)
			return d.toLocaleDateString('it-IT', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			})
		}
	}
}
</script>

<style scoped>
.media-card {
	cursor: pointer;
	transition: all 0.2s ease;
}

.media-card:hover {
	transform: translateY(-4px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.media-thumbnail {
	object-fit: cover;
}

.video-thumbnail,
.audio-thumbnail,
.document-thumbnail {
	height: 200px;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.audio-thumbnail {
	background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.document-thumbnail {
	background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}
</style>
